import React from 'react';

import { FeedbackFlag } from '@atlaskit/feedback-collector/FeedbackFlag';
import FlagGroup from '@atlaskit/flag/flag-group';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { useMicrositePage } from '../../../common/analytics/hooks/useMicrositePage';
import { SentryErrorBoundary } from '../../../common/sentry';
import { CustomFeedbackCollector } from './CustomFeedbackCollector';
import { FEEDBACK_ENTRYPOINT_ID } from './utils';

type CollectFeedbackV2Props = {
	atlassianAccountId: string;
	name: string | undefined;
	isOpen: boolean;
	onClose: () => void;
};

export const CollectFeedbackV2 = ({
	atlassianAccountId,
	name,
	isOpen,
	onClose,
}: CollectFeedbackV2Props): React.JSX.Element => {
	const [displayFlag, setDisplayFlag] = React.useState(false);
	const { fireEvent } = useAnalyticsEvents();
	const page = useMicrositePage();

	const wasOpenRef = React.useRef(false);
	React.useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			fireEvent('ui.feedbackButton.clicked.twgMicrositeHome', {
				page,
			});
		}
		wasOpenRef.current = isOpen;
	}, [isOpen, fireEvent, page]);

	const handleSubmit = React.useCallback(() => setDisplayFlag(true), []);
	const hideFlag = React.useCallback(() => setDisplayFlag(false), []);

	const isLoggedIn = Boolean(atlassianAccountId);

	return (
		<SentryErrorBoundary fallback={null} touchpointId="collect-feedback">
			{isOpen && (
				<CustomFeedbackCollector
					atlassianAccountId={atlassianAccountId}
					entrypointId={FEEDBACK_ENTRYPOINT_ID}
					name={name}
					isLoggedIn={isLoggedIn}
					onClose={onClose}
					onSubmit={handleSubmit}
				/>
			)}

			<FlagGroup onDismissed={hideFlag}>{displayFlag && <FeedbackFlag />}</FlagGroup>
		</SentryErrorBoundary>
	);
};
